// import: styles
import { Box, Grid } from '@mui/material';

// import: assets
import Coin from 'core/assets/img/credits-component/coin.webp';
import BaseImage from 'core/components/image/BaseImage';

// import: components
import BaseTypography from 'core/components/typography/BaseTypography';

// import: constants
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { oneTimePaymentPlansStyles } from 'core/layouts/components/one-time-payment-plans/OneTimePaymentPlansStyles';
import PayLinkPopUp from 'core/layouts/components/subscription-plans/components/pay-link-pop-ups/PayLinkPopUp';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { ACCOUNT, PAYMENT_ERROR, PAYMENT_SUCCESSFUL } from 'core/navigation/Routes';

// import: services
import {
	creditOptionsSelector,
	getCreditOptions,
	getCreditOptionsApiStageSelector,
	getOneTimePayLinkAsync,
} from 'core/store/reducers/profileReducer/ProfileSlice';

// import: components
// import: utils
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { thousandSeparator } from 'core/utils/numberUtils';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// import: lazy load components

function OneTimePaymentPlans({ posthogEvent = null, amplitudeEvent = null }) {
	const dispatch = useDispatch();

	const creditOptions = useSelector(creditOptionsSelector);
	const creditOptionsApiStage = useSelector(getCreditOptionsApiStageSelector);

	useEffect(() => {
		dispatch(getCreditOptions());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleAddCreditsCardClick = (card) => {
		capturePosthogEvent(posthogEvent, { pack_name: card.subtext });
		captureAmplitudeEvent(amplitudeEvent, { pack_name: card.subtext });

		const value = card.discountedPrice * 100;
		dispatch(
			getOneTimePayLinkAsync({
				value,
				successUrl: `${window.location.origin}/${ACCOUNT}/${PAYMENT_SUCCESSFUL}${!Number.isNaN(value / 100) ? `?value=${value / 100}` : ''}`,
				cancelUrl: `${window.location.origin}/${ACCOUNT}/${PAYMENT_ERROR}`,
			}),
		);
	};

	return (
		<>
			<PayLinkPopUp />

			<Box id="credits-comp" sx={oneTimePaymentPlansStyles.creditsComp}>
				<Box
					id="add-credits-section"
					sx={[
						reusableStyles.flexCol,
						{
							width: '100%',
							alignItems: 'start',
							gap: '12px',
						},
					]}
				>
					{creditOptionsApiStage === ApiStage.LOADING ? (
						<Grid
							id="add-credits-cards-comp"
							container
							spacing={2}
							sx={{ justifyContent: 'center' }}
						>
							{[1, 2, 3, 4].map((index) => (
								<Grid
									key={`loader-${index}`}
									id={`loader-${index}-id`}
									item
									xs={12}
									sm={4}
									lg={2}
									xl={2}
								>
									<Box
										key={index}
										id={`loader-${index}-credits`}
										sx={[
											oneTimePaymentPlansStyles.addCreditsCard,
											reusableStyles.skeletonLoaderStyles,
											{ height: '225px' },
										]}
									/>
								</Grid>
							))}
						</Grid>
					) : (
						<Grid
							id="add-credits-cards-comp"
							container
							spacing={2}
							sx={{ justifyContent: 'center' }}
						>
							{creditOptions.map((card) => (
								<Grid
									key={card.discountedPrice}
									id={`add-${card.credits}-credits-grid`}
									item
									xs={12}
									sm={4}
									lg={2}
									xl={2}
								>
									<Box
										key={card.discountedPrice}
										id={`add-${card.credits}-credits`}
										sx={[
											oneTimePaymentPlansStyles.addCreditsCard,
											{
												border: `1px solid ${card.highlighted ? colorPalette.purple2 : colorPalette.dark1}`,
												backgroundColor: `${card.highlighted ? colorPalette.purple5 : colorPalette.dark4}`,
											},
										]}
										onClick={(e) => {
											e.stopPropagation();
											handleAddCreditsCardClick(card);
										}}
									>
										{card.discountPercent !== 0 && (
											<Box sx={oneTimePaymentPlansStyles.mostBoughtBadge}>
												<BaseTypography
													variant="b2_caption_medium"
													styles={{ color: 'colors.black' }}
												>
													{card.discountPercent}% off
												</BaseTypography>
											</Box>
										)}
										<BaseTypography
											variant="b2_caption"
											styles={{
												color: card.highlighted ? 'colors.light1' : 'colors.rainbowBlue',
												marginTop: '15px',
											}}
										>
											{card.subtext}
										</BaseTypography>

										<Box sx={[reusableStyles.flexRow, { gap: '8px' }]}>
											{card.discountPercent !== 0 && (
												<BaseTypography
													component="span"
													variant="t1_regular"
													styles={{ color: 'colors.light4', textDecoration: 'line-through' }}
												>
													${card.originalPrice}
												</BaseTypography>
											)}
											<BaseTypography
												component="span"
												variant="h1_regular"
												styles={{ color: 'colors.light1' }}
											>
												${card.discountedPrice}
											</BaseTypography>
										</Box>

										<Box
											sx={[
												oneTimePaymentPlansStyles.divider,
												{
													borderColor: card.highlighted ? colorPalette.purple3 : colorPalette.dark2,
												},
											]}
										/>

										<Box sx={[reusableStyles.flexCol, { width: '100%', alignItems: 'center' }]}>
											<Box
												sx={[
													reusableStyles.flexRow,
													{ '& img': { height: '40px', width: 'auto' } },
												]}
											>
												<BaseImage src={Coin} alt="" />
												<BaseTypography
													variant="h1_medium"
													styles={{ color: 'colors.light1', fontWeight: 400 }}
												>
													{thousandSeparator(card.credits)}
												</BaseTypography>
											</Box>
											<BaseTypography variant="b2_caption" styles={{ color: 'colors.light4' }}>
												Credits
											</BaseTypography>
										</Box>
									</Box>
								</Grid>
							))}
						</Grid>
					)}
				</Box>
			</Box>
		</>
	);
}

export default OneTimePaymentPlans;
